<template>
  <div
    v-show="show"
    ref="modalRef"
    role="dialog"
    tabindex="-1"
    aria-modal="true"
    aria-label="Modal window"
    v-bind="$attrs"
    :class="`v-modal ${className}`"
    :style="styleClass"
    @click="closeModal"
  >
    <div class="v-modal-contain" :style="{ maxWidth: `${maxWidth}rem` }">
      <div
        :class="`v-modal-container ${modalClass}`"
        :style="styleClassContain"
        @click.stop
        @scroll="onscroll"
      >
        <slot @click.stop />
        <slot name="close" @click.stop />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import EventEmitter, { EmitterEvents } from "@/utils/eventEmitter";
import { defineComponent, ref, toRefs } from "vue";

interface MergeOptions {
  closeClickDimmed: boolean;
  closeKeyCode: number | false;
  styleModalContent: { [key: string]: unknown };
}

export default defineComponent({
  inheritAttrs: false,
  props: {
    close: { type: Function, default: () => {} },
    options: { type: Object, default: () => ({}) },
    modelValue: { type: Boolean, default: true },
    maxWidth: { type: Number, default: 30 },
    styleClass: {
      type: Object,
      default: () => ({ background: "rgba(0, 0, 0, 0.5)" }),
    },
    styleClassContain: { type: Object, default: () => ({}) },
    offEvent: { type: Boolean, default: true },
    modalClass: { type: String, default: "" },
    className: { type: String, default: "" },
    heightModal: { type: String, default: "100vh - 150px" },
  },
  emits: [
    "close",
    "before-enter",
    "enter",
    "after-enter",
    "enter-cancelled",
    "before-leave",
    "leave",
    "after-leave",
    "leave-cancelled",
    "close-modal",
    "scroll-modal",
  ],
  setup(props, context) {
    const { options, modelValue } = toRefs(props);
    const modalRef = ref(null);
    const show = ref(modelValue.value);
    const mergeOptions: MergeOptions = {
      closeClickDimmed: true,
      closeKeyCode: 27,
      styleModalContent: {},
      ...options.value,
    };
    const onTransitionEmit = {
      beforeEnter: () => context.emit("before-enter"),
      enter: () => context.emit("enter"),
      afterEnter: () => context.emit("after-enter"),
      enterCancelled: () => context.emit("enter-cancelled"),
      beforeLeave: () => context.emit("before-leave"),
      leave: () => context.emit("leave"),
      afterLeave: () => {
        context.emit("after-leave");
      },
      leaveCancelled: () => context.emit("leave-cancelled"),
    };

    return {
      mergeOptions,
      modalRef,
      onTransitionEmit,
      show,
    };
  },
  watch: {
    modelValue() {
      const isShow = this.modelValue;
      this.show = isShow;

      if (isShow) this.listeningEvent();
      else this.removeListeningEvent();
    },
  },
  mounted() {
    this.listeningEvent();
  },
  onDestoryed() {
    this.removeListeningEvent();
  },
  methods: {
    closeModal() {
      this.close();
      this.$emit("close-modal");
    },
    listeningEvent() {
      EventEmitter.on(EmitterEvents.EVENT_KEYBOARD_ESC, () => this.close());
      EventEmitter.on(
        EmitterEvents.EVENT_SWITCH_ORG,
        () => typeof close === "function" && this.close(),
      );
    },
    removeListeningEvent() {
      this.$props.offEvent &&
        EventEmitter.off(EmitterEvents.EVENT_KEYBOARD_ESC);
      EventEmitter.off(EmitterEvents.EVENT_SWITCH_ORG);
    },
    onscroll(e) {
      this.$emit("scroll-modal", e);
    },
  },
});
</script>

<style lang="scss">
.modal-manage-width {
  max-width: 60rem !important;
}

.v-modal {
  position: fixed;
  top: 56px;
  left: 0;
  z-index: 1069;
  width: 100%;
  height: 100%;
  overflow: hidden;
  outline: 0;
  display: block;

  .v-modal-contain {
    min-height: calc(100% - 3.5rem);
    max-width: 500px;
    margin: 0px auto;
    display: flex;
    align-items: center;
    position: relative;
    width: auto;

    .v-modal-container {
      border-radius: 6px;
      border: 1px solid #eff0f1;
      background-color: #fff;
      box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.05);
      width: 100%;
      max-height: calc(v-bind(heightModal));
      overflow: auto;
      overscroll-behavior: none;

      @media (max-width: 991px) {
        width: calc(100% - 20px);
        margin: 0 auto;
      }

      // @media (min-height: 1081px) {
      //   max-height: calc(v-bind(heightModal));
      // }
    }
  }
}
</style>
